import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Turtle, Camera, Save, X, User, FileText, Award, Gamepad2, Target, Clock, ArrowLeft } from 'lucide-react';
import { authService } from '../services/auth';
import { userAPI, authAPI } from '../services/api';
import { getApiUrl } from '../config/apiConfig';
import { useTheme } from '../contexts/ThemeContext';

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [nickname, setNickname] = useState('');
  const [bio, setBio] = useState('');
  const [avatarPreview, setAvatarPreview] = useState('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [points, setPoints] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [stats, setStats] = useState<any>(null);

  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser) {
      navigate('/');
      return;
    }
    loadProfile();
  }, [navigate]);

  const loadProfile = async () => {
    try {
      const res = await userAPI.getProfile();
      if (res.success && res.data) {
        const data: any = res.data;
        setNickname(data.nickname || data.username || '');
        setBio(data.bio || '');
        setAvatarPreview(data.avatar || '');
        setPoints(data.points || 0);
        setStats(data.stats || null);
      }
    } catch (err) {
      console.error('加载资料失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setMessage('图片大小不能超过5MB');
      return;
    }
    setAvatarFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setAvatarPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      // 上传头像
      if (avatarFile) {
        const formData = new FormData();
        formData.append('avatar', avatarFile);
        const token = localStorage.getItem('token');
        const uploadRes = await fetch(getApiUrl('/api/auth/avatar'), {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        const uploadData = await uploadRes.json();
        if (!uploadData.success) {
          setMessage(uploadData.error || '头像上传失败');
          setSaving(false);
          return;
        }
      }

      // 更新资料
      const res = await authAPI.updateProfile({ nickname, bio } as any);
      if (res.success) {
        setMessage('保存成功！');
        if (nickname) localStorage.setItem('username', nickname);
        setAvatarFile(null);
      } else {
        setMessage((res as any).error || '保存失败');
      }
    } catch (err: any) {
      setMessage(err.response?.data?.error || err.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-turtle-50 to-white'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ocean-600" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-turtle-50 to-white'}`}>
      {/* 导航栏 */}
      <nav className={`sticky top-0 z-50 border-b backdrop-blur-md transition-colors duration-300 ${isDark ? 'bg-gray-800/80 border-gray-700' : 'bg-white/70 border-gray-200'}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <button onClick={() => navigate('/')} className="flex items-center space-x-2">
              <ArrowLeft className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
              <Turtle className={`w-6 h-6 ${isDark ? 'text-ocean-400' : 'text-ocean-600'}`} />
              <span className={`text-lg font-bold ${isDark ? 'text-ocean-400' : 'text-ocean-600'}`}>法底捞针</span>
            </button>
            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>个人资料</span>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`rounded-2xl shadow-xl p-8 ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}
        >
          <h1 className={`text-2xl font-bold mb-8 text-center ${isDark ? 'text-white' : 'text-gray-800'}`}>
            编辑个人资料
          </h1>

          {message && (
            <div className={`mb-6 p-4 rounded-lg text-center ${message.includes('成功') ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
              {message}
            </div>
          )}

          {/* 头像 */}
          <div className="flex flex-col items-center mb-8">
            <div className="relative group">
              <div className={`w-32 h-32 rounded-full overflow-hidden border-4 ${isDark ? 'border-gray-600' : 'border-gray-200'}`}>
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <div className={`w-full h-full flex items-center justify-center ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <User className={`w-16 h-16 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                  </div>
                )}
              </div>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="absolute bottom-0 right-0 p-2 bg-ocean-600 text-white rounded-full hover:bg-ocean-700 transition shadow-lg"
              >
                <Camera className="w-5 h-5" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                className="hidden"
              />
            </div>
            <p className={`mt-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              点击相机图标更换头像
            </p>
          </div>

          {/* 表单 */}
          <div className="space-y-6">
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                <User className="inline w-4 h-4 mr-1" />
                昵称
              </label>
              <input
                type="text"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder="输入你的昵称"
                className={`w-full px-4 py-3 rounded-lg border transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-ocean-500' : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400 focus:border-ocean-500'} focus:outline-none focus:ring-2 focus:ring-ocean-500/20`}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                <FileText className="inline w-4 h-4 mr-1" />
                个性签名
              </label>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="介绍一下自己吧..."
                rows={4}
                className={`w-full px-4 py-3 rounded-lg border transition-colors resize-none ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-ocean-500' : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400 focus:border-ocean-500'} focus:outline-none focus:ring-2 focus:ring-ocean-500/20`}
              />
            </div>

            {/* 游戏统计 */}
            <div className={`rounded-xl p-6 ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
              <h3 className={`text-lg font-semibold mb-4 flex items-center ${isDark ? 'text-white' : 'text-gray-800'}`}>
                <Award className="w-5 h-5 mr-2 text-ocean-600" />
                游戏统计
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <Gamepad2 className={`w-6 h-6 mx-auto mb-1 ${isDark ? 'text-ocean-400' : 'text-ocean-600'}`} />
                  <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>{stats?.totalGames || 0}</div>
                  <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>总游戏</div>
                </div>
                <div className="text-center">
                  <Target className={`w-6 h-6 mx-auto mb-1 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                  <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>{stats?.correctGames || 0}</div>
                  <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>破解</div>
                </div>
                <div className="text-center">
                  <Clock className={`w-6 h-6 mx-auto mb-1 ${isDark ? 'text-warm-400' : 'text-warm-600'}`} />
                  <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>{stats?.totalDuration || 0}</div>
                  <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>分钟</div>
                </div>
                <div className="text-center">
                  <Award className={`w-6 h-6 mx-auto mb-1 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                  <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>{points}</div>
                  <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>积分</div>
                </div>
              </div>
            </div>

            {/* 保存按钮 */}
            <div className="flex gap-4">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 flex items-center justify-center px-6 py-3 bg-ocean-600 text-white rounded-lg hover:bg-ocean-700 transition disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                    保存中...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5 mr-2" />
                    保存资料
                  </>
                )}
              </button>
              <button
                onClick={() => navigate('/')}
                className={`px-6 py-3 rounded-lg transition font-medium ${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                <X className="w-5 h-5 mr-2 inline" />
                取消
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProfilePage;
